import { getJWT, decodeJWT } from '@bynder/js-access-token-lib';
import React, { useState, useEffect, useContext } from 'react';

// Taken from https://github.com/ibitcy/eo-locale/blob/eee6f2e5201178f5ee484047d6d9d0251cd5e31d/packages/core/src/parser/input_stream.ts
class InputStream {
    constructor(input) {
        this.input = input;
        this.index = 0;
        this.done = false;
        this.value = '';
        this.value = this.input.charAt(0);
    }
    next() {
        if (++this.index >= this.input.length) {
            this.done = true;
        }
        return (this.value = this.input.charAt(this.index));
    }
    croak() {
        throw new Error(`[${this.input}]. Unexpected character "${this.value}" on position ${this.index}.`);
    }
}

// Taken from https://github.com/ibitcy/eo-locale/blob/eee6f2e5201178f5ee484047d6d9d0251cd5e31d/packages/core/src/parser/parser.ts
var TokenType;
(function (TokenType) {
    TokenType[TokenType["Plural"] = 0] = "Plural";
    TokenType[TokenType["Select"] = 1] = "Select";
    TokenType[TokenType["Text"] = 2] = "Text";
    TokenType[TokenType["Variable"] = 3] = "Variable";
})(TokenType || (TokenType = {}));
const OPEN = '{';
const CLOSE = '}';
const DELIMITER = ',';
const PUNC_SYMBOLS = [OPEN, CLOSE, DELIMITER];
const PLURAL_IDENTIFIER = 'plural';
const SELECT_IDENTIFIER = 'select';
class TokenStream {
    constructor(translation) {
        this.input = new InputStream(translation);
    }
    next() {
        if (this.input.value === OPEN) {
            return this.readVariable();
        }
        return this.readText();
    }
    skip(ch) {
        if (ch !== this.input.value) {
            this.input.croak();
        }
        this.input.next();
    }
    readWhile(predicate) {
        let str = '';
        while (!this.input.done && predicate(this.input.value)) {
            str += this.input.value;
            this.input.next();
        }
        return str;
    }
    readVariable() {
        this.skip(OPEN);
        const value = this.readWhile((ch) => !isPunc(ch)).trim();
        if (value.length === 0) {
            this.input.croak();
        }
        if (this.input.value === CLOSE) {
            this.skip(CLOSE);
            return {
                type: TokenType.Variable,
                value,
            };
        }
        this.skip(DELIMITER);
        const type = this.readVariableType();
        return {
            options: this.readVariableOptions(),
            type,
            value,
        };
    }
    readText() {
        return {
            type: TokenType.Text,
            value: this.readWhile((ch) => ch !== OPEN && ch !== CLOSE),
        };
    }
    readVariableType() {
        const type = this.readWhile((ch) => ch !== DELIMITER).trim();
        if (type === PLURAL_IDENTIFIER) {
            return TokenType.Plural;
        }
        if (type === SELECT_IDENTIFIER) {
            return TokenType.Select;
        }
        this.input.croak();
    }
    readVariableOptions() {
        this.skip(DELIMITER);
        const options = {};
        while (this.input.value !== CLOSE) {
            options[this.readText().value.trim()] = this.readExpression();
        }
        this.skip(CLOSE);
        return options;
    }
    readExpression() {
        const tokens = [];
        this.skip(OPEN);
        while (this.input.value !== CLOSE) {
            tokens.push(this.next());
        }
        this.skip(CLOSE);
        return tokens;
    }
}
function isPunc(ch) {
    return PUNC_SYMBOLS.includes(ch);
}

const wordReplacements = [
    'campaigns',
    'Campaigns',
    'Campaign',
    'campaign',
    'Collection',
    'collections',
    'collection',
    'Collections',
    'limitedUsage',
    'LIMITED',
    'jobs',
    'Jobs',
    'job',
    'Job',
    'Guidelines',
    'WorkflowCalendar',
    'AssetBank',
    'Brandstore',
    'Reporter',
    'Brand',
    'brands',
    'categories',
    'Brands',
    'brand',
    'Asset',
    'Subbrand',
    'subbrands',
    'Assets',
    'assets',
    'asset',
    'subbrand',
    'Subbrands',
    'category',
    'task',
    'Stage',
    'accountable',
    'tasks',
    'stage',
    'Categories',
    'Category',
    'stages',
    'Task',
    'Stages',
    'Tasks',
    'Accountable',
];

function getTranslationParts({ locale, translation, values, wordReplacements: wordReplacements$1, }) {
    const tokenStream = new TokenStream(translation);
    const result = [];
    while (!tokenStream.input.done) {
        applyToken(tokenStream.next());
    }
    return result;
    function applyToken({ options, type, value }) {
        if (type === TokenType.Variable) {
            if (wordReplacements.includes(value)) {
                if (wordReplacements$1[value]) {
                    result.push(wordReplacements$1[value]);
                }
                else {
                    result.push(value);
                }
                return;
            }
            result.push(values[value]);
            return;
        }
        if (!options) {
            result.push(value);
            return;
        }
        if (type === TokenType.Select) {
            options[values[value]].map(applyToken);
            return;
        }
        const normalizedLocale = locale.replace('_', '-');
        options[new Intl.PluralRules(normalizedLocale).select(values[value])].map(applyToken);
    }
}

function getLocale(locale) {
    return locale || 'en_US';
}

class Localization {
    constructor(translations, locale, customTranslations) {
        this.memo = {};
        this.onError = console.error;
        this.translateParts = (id, values) => {
            const translation = this.getTranslationById(id);
            try {
                return getTranslationParts({
                    locale: this.locale,
                    translation,
                    values,
                    wordReplacements: this.wordReplacements,
                });
            }
            catch (error) {
                this.onError(error);
                return [translation];
            }
        };
        this.translate = (id, values = {}) => {
            return this.translateParts(id, values).join('');
        };
        this.getTranslationById = (id) => {
            if (!this.memo[id]) {
                let translation = this.translations[id];
                if (typeof translation !== 'string') {
                    this.onError(new Error(`Localization: Missing translation with ID "${id}"`));
                    translation = id;
                }
                this.memo[id] = translation;
            }
            return this.memo[id];
        };
        this.locale = getLocale(locale);
        if (customTranslations) {
            this.translations = {
                ...translations,
                ...customTranslations.translations,
            };
            this.wordReplacements = customTranslations.wordReplacements;
        }
        else {
            this.translations = {
                ...translations,
            };
            this.wordReplacements = {};
        }
    }
}

/**
 * Better types for `Object.entries`.
 *
 * @example
 * getEntries(object).map(([key, value]) => {
 * 	return {
 * 		key: value,
 * 	};
 * });
 *
 * @see https://stackoverflow.com/a/74823834
 */
const getEntries = (obj) => Object.entries(obj);

async function fetchCustomTranslations(options) {
    const emptyCustomTranslations = {
        translations: {},
        wordReplacements: {},
    };
    if (!options.addCustomTranslations) {
        return emptyCustomTranslations;
    }
    try {
        const portalId = await getPortalId();
        const translationsUrl = getCustomTranslationsUrl(portalId);
        const response = await fetch(translationsUrl, {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json',
            },
        });
        if (response.ok) {
            const json = await response.json();
            return composeCustomTranslations(json, getLocale(options.locale));
        }
        const errorMessage = await response.text();
        throw new Error(errorMessage);
    }
    catch (error) {
        console.error('Localization: Failed to fetch custom translations.', error);
        return emptyCustomTranslations;
    }
}
function getCustomTranslationsUrl(portalId) {
    return `/v7/portal/portals/${portalId}/custom-translations`;
}
async function getPortalId() {
    try {
        const jwt = await getJWT();
        const decodedJwt = decodeJWT(jwt);
        return decodedJwt.account_id;
    }
    catch (error) {
        const userLessToken = await fetch('/v7/idp/access-tokens/userless-by-session')
            .then((response) => response.json())
            .then((data) => data.access_token);
        const decodedToken = decodeJWT(userLessToken);
        return decodedToken.account_id;
    }
}
function composeCustomTranslations(settings, locale) {
    var _a;
    const customTranslations = {
        translations: ((_a = settings.customTranslations) === null || _a === void 0 ? void 0 : _a[locale]) || {},
        wordReplacements: {},
    };
    for (const [word, replacement] of getEntries(settings.wordReplacements)) {
        if (typeof replacement === 'string' && replacement.trim().length > 0) {
            customTranslations.wordReplacements[word] = replacement;
        }
    }
    return customTranslations;
}

async function fetchTranslations(options) {
    if (!options.forceFallback) {
        if (process.env.NODE_ENV === 'production' || process.env.NODE_ENV === 'development') {
            if (options.locale === 'en_US') {
                return fetchFallback(options.fetchFallback);
            }
            const [fallbackTranslations, translations] = await Promise.all([
                fetchFallback(options.fetchFallback),
                fetchTranslationsFromService(options),
            ]);
            // Translations from the service override English translations
            return {
                ...fallbackTranslations,
                ...translations,
            };
        }
    }
    /* This is for a test environment and any other unforseen environments */
    return fetchFallback(options.fetchFallback);
}
function fetchFallback(fetchFn) {
    try {
        return fetchFn();
    }
    catch (error) {
        console.error('Localization: Failed to fetch fallback.', error);
        return Promise.resolve({});
    }
}
async function fetchTranslationsFromService(options) {
    try {
        const url = composeUrl(options);
        const config = {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json',
            },
        };
        const response = await fetch(url, config);
        if (response.ok) {
            return response.json();
        }
        const errorMessage = await response.text();
        throw new Error(errorMessage);
    }
    catch (error) {
        console.error('Localization: Failed to fetch from the service.', error);
        return {};
    }
}
function composeUrl(options) {
    var _a;
    const parameters = new URLSearchParams({
        module: options.project,
        locale: getLocale(options.locale),
    });
    if ((_a = options.features) === null || _a === void 0 ? void 0 : _a.length) {
        parameters.append('features', options.features.join(','));
    }
    return '/v7/localization/keys/?' + parameters;
}

async function getLocalization(userOptions) {
    const options = {
        ...userOptions,
        locale: getLocale(userOptions.locale),
    };
    const [translations, customTranslations] = await Promise.all([
        fetchTranslations(options),
        fetchCustomTranslations(options),
    ]);
    const { translate } = new Localization(translations, options.locale, customTranslations);
    return {
        translate,
    };
}

const LocalizationContext = React.createContext(undefined);
function LocalizationProvider({ children, translations: translationsProp, onError, ...options }) {
    const [translations, setTranslations] = useState(translationsProp);
    const [customTranslations, setCustomTranslations] = useState({
        translations: {},
        wordReplacements: {},
    });
    useEffect(() => {
        if (!translations) {
            Promise.all([fetchTranslations(options), fetchCustomTranslations(options)]).then(([translationsResponse, customTranslationsResponse]) => {
                setTranslations(translationsResponse);
                setCustomTranslations(customTranslationsResponse);
            });
        }
    }, [translations, options]);
    if (!translations) {
        return null;
    }
    // eslint-disable-next-line react/jsx-no-constructed-context-values
    const localization = new Localization(translations, options.locale, customTranslations);
    if (onError) {
        localization.onError = onError;
    }
    return (React.createElement(LocalizationContext.Provider, { value: localization }, children));
}

function Translate({ id, ...values }) {
    const localization = useContext(LocalizationContext);
    if (!localization) {
        throw new Error('Localization: <Translate /> cannot be used without <LocalizationProvider />');
    }
    return (React.createElement(React.Fragment, null, localization.translateParts(id, values).map((part, index) => {
        /* eslint-disable react/no-array-index-key */
        if (React.isValidElement(part)) {
            return React.cloneElement(part, {
                key: index,
            });
        }
        return React.createElement(React.Fragment, { key: index }, part);
    })));
}

function useTranslate() {
    const localization = useContext(LocalizationContext);
    if (!localization) {
        throw new Error('Localization: useTranslate() cannot be used without <LocalizationProvider />');
    }
    return {
        translate: localization.translate,
    };
}

export { Localization, LocalizationProvider, Translate, getLocalization, useTranslate };
//# sourceMappingURL=index.js.map
